/**
 * The words the fleet screens use.
 *
 * Kept together so the same value is never called two different things on two
 * screens, and so translating later is a change in one file.
 */

import type {
  Availability,
  ExtraUnit,
  Fuel,
  Ownership,
  RateType,
  Transmission,
  TravelRange,
  TripArea,
  VehicleCategory,
  VehicleStatus,
} from '@/api/types';
import type { StatusPresentation } from '@fleet/shared';

export const CATEGORY_LABELS: Readonly<Record<VehicleCategory, string>> = {
  hatchback: 'Hatchback',
  sedan: 'Sedan',
  suv: 'SUV',
  jeep: 'Jeep',
  van: 'Van',
  minibus: 'Minibus',
};

export const TRANSMISSION_LABELS: Readonly<Record<Transmission, string>> = {
  manual: 'Manual',
  automatic: 'Automatic',
};

export const FUEL_LABELS: Readonly<Record<Fuel, string>> = {
  petrol: 'Petrol',
  diesel: 'Diesel',
  electric: 'Electric',
  hybrid: 'Hybrid',
};

export const OWNERSHIP_LABELS: Readonly<Record<Ownership, string>> = {
  owned: 'Owned',
  partner: 'Partner',
};

export const RATE_TYPE_LABELS: Readonly<Record<RateType, string>> = {
  per_day: 'Per day',
  per_hour: 'Per hour',
  per_trip: 'Per trip',
};

export const EXTRA_UNIT_LABELS: Readonly<Record<ExtraUnit, string>> = {
  per_booking: 'Per booking',
  per_day: 'Per day',
  per_km: 'Per kilometre',
};

export const TRAVEL_RANGE_LABELS: Readonly<Record<TravelRange, string>> = {
  anywhere: 'Anywhere',
  inside_only: 'Valley only',
};

export const TRIP_AREA_LABELS: Readonly<Record<TripArea, string>> = {
  inside_valley: 'Inside the valley',
  outside_valley: 'Outside the valley',
};

/**
 * What the fleet list says, derived from the bookings.
 *
 * "Off road" is gone from the counter's vocabulary: it was engineering's word
 * for two different situations, and a vehicle that had gone out on a booking
 * still showed as free because nothing updated the row. `on_use` is computed
 * from the bookings and cannot be forgotten.
 */
/**
 * Whether a driver could be sent out right now.
 *
 * The column used to read "Working / Left", which is an employment fact.
 * Whoever is looking at this screen is trying to find somebody for this
 * afternoon, and a driver who is out on a trip is not available however
 * employed they are.
 */
export const DRIVER_AVAILABILITY: Readonly<Record<string, StatusPresentation>> = {
  available: { label: 'Available', tone: 'active', shape: 'square' },
  on_trip: { label: 'On a trip', tone: 'neutral', shape: 'bar' },
  left: { label: 'Left', tone: 'neutral', shape: 'cross' },
};

export const AVAILABILITY: Readonly<Record<Availability, StatusPresentation>> = {
  available: { label: 'Available', tone: 'active', shape: 'square' },
  on_use: { label: 'On use', tone: 'neutral', shape: 'bar' },
  servicing: { label: 'Servicing', tone: 'attention', shape: 'ring' },
  not_available: { label: 'Not available', tone: 'alert', shape: 'cross' },
};

/**
 * The status somebody sets by hand, on the vehicle form.
 *
 * Shorter than it was, and worded for what it does rather than where the
 * vehicle is: "on trip" is not something anybody should be setting manually
 * now that the bookings answer that question themselves.
 */
export const VEHICLE_STATUS_LABELS: Readonly<Record<VehicleStatus, string>> = {
  available: 'In the fleet',
  on_trip: 'On use',
  servicing: 'Servicing',
  off_road: 'Not available',
};

/** Colour plus a word plus a shape. Never colour alone. */
export const VEHICLE_STATUS: Readonly<Record<VehicleStatus, StatusPresentation>> = {
  available: { label: 'Available', tone: 'active', shape: 'square' },
  on_trip: { label: 'On use', tone: 'neutral', shape: 'bar' },
  servicing: { label: 'Servicing', tone: 'attention', shape: 'ring' },
  off_road: { label: 'Not available', tone: 'alert', shape: 'cross' },
};

export function optionsFrom<T extends string>(
  labels: Readonly<Record<T, string>>,
): readonly { value: T; label: string }[] {
  return (Object.keys(labels) as T[]).map((value) => ({ value, label: labels[value] }));
}

/**
 * What to call each vehicle in a list, by name, without ambiguity.
 *
 * The client asked for vehicle names everywhere outside the fleet screen: a
 * plate is hard to remember and nobody at the counter says "BA 2 CHA 1234",
 * they say "the Hiace". But two owned Hiaces are both "Toyota Hiace", and a
 * dropdown with the same line twice is a coin toss. So the name stands alone
 * when it is unique, and only a name shared with another vehicle gets its plate
 * appended — the plate turns up exactly where it is needed and nowhere else.
 *
 * Partner vehicles already carry the vendor in their name, which separates
 * most of them before this has to.
 */
export function distinctVehicleNames(
  vehicles: readonly { readonly id: string; readonly name: string; readonly plate: string }[],
): ReadonlyMap<string, string> {
  const uses = new Map<string, number>();
  for (const vehicle of vehicles) uses.set(vehicle.name, (uses.get(vehicle.name) ?? 0) + 1);
  return new Map(
    vehicles.map((vehicle) => [
      vehicle.id,
      (uses.get(vehicle.name) ?? 0) > 1 ? `${vehicle.name} (${vehicle.plate})` : vehicle.name,
    ]),
  );
}
